import ApiService from "@/core/services/ApiService";
import JwtService from "@/core/services/JwtService";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";

export interface User {
  name: string;
  surname: string;
  email: string;
  password: string;
  api_token: string;
}

export interface UserAuthInfo {
  errors: unknown;
  user: User;
  isAuthenticated: boolean;
  settings: object;
}

export interface searchSettings {
  settings: object;
}

export interface selectedLocation {
  location: string;
}

export interface notifications {
  numberUnreadNotifications: array;
}

@Module
export default class AuthModule extends VuexModule implements UserAuthInfo {
  errors = {};
  user = {} as User;
  isAuthenticated = !!JwtService.getToken();
  settings = {} as searchSettings;
  location = "" as selectedLocation;
  numberUnreadNotifications = [] as notifications;

  /**
   * Get current user object
   * @returns User
   */
  get currentUser(): User {
    return this.user;
  }

  get currentLocation(): selectedLocation {
    return this.location;
  }

  get currentNumberUnreadNotifications(): notifications {
    return this.numberUnreadNotifications;
  }

  get currentSettings(): searchSettings {
    return this.settings;
  }

  /**
   * Verify user authentication
   * @returns boolean
   */
  get isUserAuthenticated(): boolean {
    return this.isAuthenticated;
  }

  /**
   * Get authentification errors
   * @returns array
   */
  get getErrors() {
    return this.errors;
  }

  @Mutation
  [Mutations.SET_ERROR](error) {
    this.errors = { ...error };
  }

  @Mutation
  [Mutations.SET_AUTH](user) {
    this.isAuthenticated = true;
    this.user = user;
    this.errors = {};
    JwtService.saveToken(user.data.token);
  }

  @Mutation
  [Mutations.SET_USER](user) {
    this.user = user;
  }

  @Mutation
  [Mutations.SET_PASSWORD](password) {
    this.user.password = password;
  }

  @Mutation
  [Mutations.PURGE_AUTH]() {
    this.isAuthenticated = false;
    this.user = {} as User;
    this.errors = [];
    JwtService.destroyToken();
  }

  @Mutation
  [Mutations.SET_SETTINGS](settings) {
    this.settings = settings;
  }

  @Mutation
  [Mutations.SET_LOCATION](data) {
    this.location = data;
  }

  @Mutation
  [Mutations.SET_NUMBER_UNREAD_NOTIFICATIONS](data) {
    this.numberUnreadNotifications = data;
  }

  @Action
  [Actions.SETTINGS_UPDATE](settings) {
    return this.context.commit(Mutations.SET_SETTINGS, settings);
  }

  @Action
  [Actions.LOCATION](data) {
    this.context.commit(Mutations.SET_LOCATION, data);
  }

  @Action
  [Actions.NUMBER_UNREAD_NOTIFICATIONS](data) {
    this.context.commit(Mutations.SET_NUMBER_UNREAD_NOTIFICATIONS, data);
  }

  @Action
  [Actions.LOGIN](credentials) {
    return ApiService.post("admin/login", credentials)
      .then(({ data }) => {
        this.context.commit(Mutations.SET_AUTH, data);
      })
      .catch(({ response }) => {  
        this.context.commit(Mutations.SET_ERROR, response.data);
      });
  }

  @Action
  [Actions.LOGOUT]() {
    this.context.commit(Mutations.PURGE_AUTH);
  }

  @Action
  [Actions.REGISTER](credentials) {
    return ApiService.post("admin/register", credentials)
      .then(({ data }) => {
        this.context.commit(Mutations.SET_AUTH, data);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, response.data);
      });
  }

  @Action
  [Actions.FORGOT_PASSWORD](payload) {
    return ApiService.post("forgot_password", payload)
      .then(() => {
        this.context.commit(Mutations.SET_ERROR, {});
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, response.data);
      });
  }

  @Action
  [Actions.VERIFY_AUTH](payload) {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      
      ApiService.post("admin/verify-token", {...payload})
        .then(({ data }) => {
          this.context.commit(Mutations.SET_AUTH, data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response.data);
          this.context.commit(Mutations.PURGE_AUTH);
        });
    } else {
      this.context.commit(Mutations.PURGE_AUTH);
    }
  }
}
